import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import {
    fetchCatalogItems,
    fetchGlobalCatalogItems,
} from 'entities/CatalogItem';
import { catalogItemActions } from 'entities/CatalogItem/model/slices/CatalogItemSlice';
import { categoryActions } from 'entities/Category/model/slices/CategorySlice';
import { getSelectedPropertyCatalogId } from 'entities/Property/model/selectors/PropertySelectors';
import { addCatalogItemActions } from 'features/AddCatalogItem/model/slices/AddCatalogItemSlice';
import { getSearchLocalCatalogItemValue } from 'features/SearchCatalogItems/model/selectors/searchCatalogItemsSelectors';
import { searchCatalogItemsActions } from 'features/SearchCatalogItems/model/slices/SearchCatalogItemsSlice';
import {
    getRouteGlobalCatalog,
    getRoutePropertiesCatalog,
} from 'shared/const/router';
import { useAppDispatch } from 'shared/lib/hooks/useAppDispatch';
import { useFetchWIthController } from 'shared/lib/hooks/useFetchWIthController';

type NavigationType = 'global-marketplace' | 'property-catalog';

export const useMarketplaceNavigation = (
    type: NavigationType = 'global-marketplace',
) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const params = useParams();
    const localSearchValue = useSelector(getSearchLocalCatalogItemValue);
    const catalogId = useSelector(getSelectedPropertyCatalogId);
    const fetchWithController = useFetchWIthController();

    const navigateToMarketplace = useCallback(() => {
        const propertyId = params.propertyId;
        if (!propertyId) return;

        if (type === 'global-marketplace') {
            dispatch(searchCatalogItemsActions.setActive(true));
            dispatch(addCatalogItemActions.setEditItemMode(true));
            dispatch(
                searchCatalogItemsActions.setGlobalCatItemSearchValue(
                    localSearchValue,
                ),
            );
            dispatch(categoryActions.clearCategorySelection());
            navigate(getRouteGlobalCatalog(propertyId));

            if (localSearchValue) {
                fetchWithController((controller: AbortController) =>
                    dispatch(
                        fetchGlobalCatalogItems({
                            searchString: localSearchValue,
                            propertyId,
                            abortController: controller,
                        }),
                    ),
                );
            }
        } else {
            if (localSearchValue) {
                dispatch(searchCatalogItemsActions.setSkipReset(true));
                dispatch(searchCatalogItemsActions.setActive(true));
                dispatch(
                    searchCatalogItemsActions.setLocalCatItemSearchValue(
                        localSearchValue,
                    ),
                );
                dispatch(catalogItemActions.setLoading(true));
            }
            navigate(getRoutePropertiesCatalog(propertyId));

            if (localSearchValue && catalogId) {
                fetchWithController((controller: AbortController) =>
                    dispatch(
                        fetchCatalogItems({
                            data: {
                                catalogId,
                                searchStr: localSearchValue,
                                isInventory: false,
                            },
                            abortController: controller,
                        }),
                    ),
                );
            }
        }
    }, [
        dispatch,
        navigate,
        params.propertyId,
        localSearchValue,
        catalogId,
        fetchWithController,
        type,
    ]);

    return { navigateToMarketplace };
};
